import { scry, scryImageUrl } from "@/lib/scryClient";
import { toast } from "@/components/ui/use-toast";
import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { X, Plus, Heart, Share2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import AddToCollectionModal from "@/components/AddToCollectionModal";
import {
  collectionGroup,
  getDocs,
  query,
  where,
  addDoc,
  collection,
  serverTimestamp,
  deleteDoc,
  doc,
} from "firebase/firestore"; // unified
import { auth } from "@/lib/firebase";
import { db } from "@/lib/firebase";
import { useHighlightCollectionNames } from "@/hooks/useHighlightCollectionNames";

/** minimal card shape used inside this modal */
type ScryfallCard = {
  id: string;
  name: string;
  oracle_id?: string;
  set?: string;
  set_name?: string;
  collector_number?: string;
  image_uris?: { small?: string; normal?: string; large?: string };
  card_faces?: Array<{
    image_uris?: { small?: string; normal?: string; large?: string };
  }>;
  prices?: {
    usd?: string | null;
    usd_foil?: string | null;
    usd_etched?: string | null;
  };
  foil?: boolean;
  nonfoil?: boolean;
  finishes?: string[];
  rarity?: string;
};

type Variant = {
  set?: string | null;
  set_name?: string | null;
  id: string;
  name: string;
  oracle_id?: string;
  collector_number?: string;
  image_uris?: { small?: string; normal?: string; large?: string };
  card_faces?: Array<{
    image_uris?: { small?: string; normal?: string; large?: string };
  }>;
  prices?: {
    usd?: string | null;
    usd_foil?: string | null;
    usd_etched?: string | null;
  };
  foil?: boolean;
  nonfoil?: boolean;
  finishes?: string[];
  rarity?: string;
};

type Props = {
  open?: boolean;
  baseCard?: { id: string } | null;
  onClose?: () => void;
  variants?: Variant[] | null; // external override (optional)
};

export default function VariantsModal({
  open = true,
  baseCard = null,
  onClose,
  ...props
}: Props) {
  const params = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const [base, setBase] = useState<ScryfallCard | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [addFor, setAddFor] = useState<ScryfallCard | null>(null);
  const [selectedPriceRegular, setSelectedPriceRegular] = useState<number | null>(null);
  const [selectedPriceFoil, setSelectedPriceFoil] = useState<number | null>(null);
  // Local variants state (do NOT write into props)
  const [variants, setVariants] = useState<Variant[] | null>(props.variants ?? null);
  const [selectedSet, setSelectedSet] = useState<string>("all");

  const [marketOpen, setMarketOpen] = useState(true);
  const [marketListings, setMarketListings] = useState<any[]>([]);

  // Wishlist state parity with V2Modal
  const [wishlisted, setWishlisted] = useState(false);
  const [wishlistDocId, setWishlistDocId] = useState<string | null>(null);

  // Highlight variants that exist anywhere (sale/trade)
  const { highlightNames } = useHighlightCollectionNames(variants, { tryLowerField: true });

  // Zoom/view card (reuse addFor)
  const [zoomCard, setZoomCard] = useState<ScryfallCard | null>(null);

  const cardIdFromRoute = params.cardId || baseCard?.id || null;

  /** fetch all prints (handles pagination + extras/variations) */
  useEffect(() => {
    let cancelled = false;
    // if parent injected variants, use those
    if (props.variants && Array.isArray(props.variants)) {
      setVariants(props.variants);
    }

    async function fetchAllPrints(oracleId: string): Promise<ScryfallCard[]> {
      const acc: ScryfallCard[] = [];
      const query = `oracleid:${oracleId}`;

      const data = await scry.search(query, { unique: "prints", order: "released" });

      const page: ScryfallCard[] = (data.data || []).map((d: any) => ({
        id: d.id,
        name: d.name,
        oracle_id: d.oracle_id,
        set: d.set,
        set_name: d.set_name,
        collector_number: d.collector_number,
        image_uris: d.image_uris,
        card_faces: d.card_faces,
        prices: d.prices,
        foil: d.foil,
        nonfoil: d.nonfoil,
        finishes: d.finishes,
        rarity: d.rarity,
      }));
      acc.push(...page);

      return acc;
    }

    async function load() {
      if (!cardIdFromRoute) return;
      setLoading(true);
      setError(null);
      try {
        const baseRes = await fetch(
          `https://api.scryfall.com/cards/${encodeURIComponent(cardIdFromRoute)}`
        );
        if (!baseRes.ok) throw new Error("Failed to load card");
        const baseJson = (await baseRes.json()) as ScryfallCard;
        if (cancelled) return;
        setBase(baseJson);

        const oid = baseJson.oracle_id;
        if (!oid) {
          if (!cancelled) setVariants([baseJson as unknown as Variant]);
        } else {
          const all = await fetchAllPrints(oid);
          if (!cancelled) {
            all.sort((a, b) => {
              const da = a.released_at ? Date.parse(a.released_at) : 0;
              const db = b.released_at ? Date.parse(b.released_at) : 0;
              if (db !== da) return db - da;
              const sa = (a.set || "");
              const sb = (b.set || "");
              if (sb !== sa) return sb.localeCompare(sa);
              const ca = parseInt(a.collector_number || "0", 10) || 0;
              const cb = parseInt(b.collector_number || "0", 10) || 0;
              return cb - ca;
            });
            setVariants(all as unknown as Variant[]);
            resolveHighlights(all as Variant[]);
          }
        }
      } catch (e: any) {
        if (!cancelled) {
          setBase(null);
          setVariants([]);
          setError(e?.message ?? "Could not load variants");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    void load();
    return () => {
      cancelled = true;
    };
  }, [cardIdFromRoute]);

  // Marketplace listings effect
  useEffect(() => {
    if (!baseCard?.name) { setMarketListings([]); return; }
    let cancelled = false;
    (async () => {
      try {
        const snap = await getDocs(
          query(
            collectionGroup(db, "cards"),
            where("name", "==", baseCard.name)
          )
        );
        if (cancelled) return;
        const rows: any[] = [];
        snap.forEach((d) => {
          const v: any = d.data();
          if (v.status !== "sale" && v.status !== "trade") return;
          const ownerUid = d.ref.path.split("/")[1];
          rows.push({
            id: d.id,
            ownerUid,
            name: v.name,
            image:
              v.custom_image_url ||
              v.image ||
              v.scryfall_full?.image_uris?.normal ||
              null,
            set: v.set,
            number: v.number,
              status: v.status,
            price: typeof v.price === "number" ? v.price : null,
            scryfall_id: v.scryfall_id || null,
            foil: !!v.foil,
          });
        });
        setMarketListings(rows.slice(0, 60));
      } catch {
        if (!cancelled) setMarketListings([]);
      }
    })();
    return () => { cancelled = true; };
  }, [baseCard?.name]);

  // Add after variants are set (for injected variants too)
  useEffect(() => {
    if (props.variants && Array.isArray(props.variants)) {
      const sorted = [...props.variants].sort((a: any, b: any) => {
        const da = a.released_at ? Date.parse(a.released_at) : 0;
        const db = b.released_at ? Date.parse(b.released_at) : 0;
        if (db !== da) return db - da;
        const sa = (a.set || "");
        const sb = (b.set || "");
        if (sb !== sa) return sb.localeCompare(sa);
        const ca = parseInt(a.collector_number || "0", 10) || 0;
        const cb = parseInt(b.collector_number || "0", 10) || 0;
        return cb - ca;
      });
      setVariants(sorted);
      resolveHighlights(sorted as any);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.variants]);

  // Highlight resolver
  async function resolveHighlights(list: Variant[]) {
    if (!list?.length) { setHighlightNames(new Set()); return; }
    const norm = (s: string) => s.toLowerCase();
    const nameMap = new Map<string, string>();
    list.forEach(v => {
      if (!v.name) return;
      const n = norm(v.name);
      if (!nameMap.has(n)) nameMap.set(n, v.name);
    });
    const lowers = Array.from(nameMap.keys());
    const hits = new Set<string>();

    for (let i = 0; i < lowers.length; i += 10) {
      const chunkLower = lowers.slice(i, i + 10);
      const originals = chunkLower.map(l => nameMap.get(l) || l);
      let success = false;
      try {
        const snap = await getDocs(
          query(collectionGroup(db, "cards"), where("name_lower", "in", chunkLower))
        );
        success = true;
        snap.forEach(d => {
          const v: any = d.data();
          const s = (v.status || "").toLowerCase();
          if ((s === "sale" || s === "trade" || v.for_sale || v.for_trade) && v.name) {
            hits.add(norm(v.name));
          }
        });
      } catch { /* fallback */ }
      if (!success) {
        try {
          const snap = await getDocs(
            query(collectionGroup(db, "cards"), where("name", "in", originals))
          );
          snap.forEach(d => {
            const v: any = d.data();
            const s = (v.status || "").toLowerCase();
            if ((s === "sale" || s === "trade" || v.for_sale || v.for_trade) && v.name) {
              hits.add(norm(v.name));
            }
          });
        } catch { /* ignore */ }
      }
    }
    setHighlightNames(hits);
  }

  // Wishlist detect for current base card id
  useEffect(() => {
    let cancel = false;
    (async () => {
      if (!auth.currentUser || !base?.id) { setWishlisted(false); setWishlistDocId(null); return; }
      try {
        const snap = await getDocs(
          query(
            collection(db, "users", auth.currentUser.uid, "wishlist"),
            where("scryfall_id", "==", base.id)
          )
        );
        if (cancel) return;
        if (!snap.empty) {
          setWishlisted(true);
          setWishlistDocId(snap.docs[0].id);
        } else {
          setWishlisted(false);
          setWishlistDocId(null);
        }
      } catch {
        if (!cancel) { setWishlisted(false); setWishlistDocId(null); }
      }
    })();
    return () => { cancel = true; };
  }, [base?.id, auth.currentUser?.uid]);

  async function toggleWishlist(c: ScryfallCard) {
    if (!auth.currentUser) { toast({ description: "Sign in to manage wishlist." }); return; }
    const uid = auth.currentUser.uid;
    if (wishlisted && wishlistDocId) {
      try {
        await deleteDoc(doc(db, "users", uid, "wishlist", wishlistDocId));
        setWishlisted(false);
        setWishlistDocId(null);
        toast({ description: "Removed from wishlist." });
      } catch (e: any) {
        toast({ description: e?.message || "Could not remove." });
      }
      return;
    }
    try {
      const img =
        c.image_uris?.normal ??
        c.card_faces?.[0]?.image_uris?.normal ??
        (c.id ? scryImageUrl(c.id, "normal") : null);
      const ref = await addDoc(collection(db, "users", uid, "wishlist"), {
        scryfall_id: c.id,
        oracle_id: c.oracle_id ?? null,
        name: c.name,
        set: c.set ?? null,
        number: c.collector_number ?? null,
        image: img,
        card_faces: c.card_faces ?? null,
        foil: c.finishes?.includes("foil") ? true : false,
        condition: "NM",
        desired_qty: 1,
        createdAt: serverTimestamp(),
      });
      setWishlisted(true);
      setWishlistDocId(ref.id);
      toast({ description: `Added “${c.name}” to wishlist ✓` });
    } catch (e: any) {
      toast({ description: e?.message || "Could not add to wishlist." });
    }
  }

  // ---- Shared helpers (moved outside component) ----
  function getCardImageUrl(card: any): string {
    if (!card) return "/images/cardback.png";
    const face = card.card_faces?.[0]?.image_uris;
    return (
      face?.large ||
      card.image_uris?.large ||
      face?.normal ||
      card.image_uris?.normal ||
      face?.small ||
      card.image_uris?.small ||
      "/images/cardback.png"
    );
  }

  async function copyImageBlob(url: string): Promise<"image" | "url" | "fail"> {
    if (!url) return "fail";
    try {
      const res = await fetch(url, { mode: "cors", cache: "force-cache" });
      if (!res.ok) throw new Error("fetch failed");
      const blob = await res.blob();
      // @ts-ignore
      if (navigator.clipboard && navigator.clipboard.write && window.ClipboardItem) {
        // @ts-ignore
        const item = new ClipboardItem({ [blob.type]: blob });
        // @ts-ignore
        await navigator.clipboard.write([item]);
        return "image";
      }
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(url);
        return "url";
      }
    } catch {
      try {
        if (navigator.clipboard?.writeText) {
          await navigator.clipboard.writeText(url);
          return "url";
        }
      } catch {}
    }
    return "fail";
  }

  // ADD: handler actually used by the Copy Img button (was missing -> click did nothing / ReferenceError)
  async function handleCopyImage(card: ScryfallCard | null) {
    if (!card) return;
    const url = getCardImageUrl(card);
    const mode = await copyImageBlob(url);
    if (mode === "image") {
      toast({ description: "Image copied to clipboard ✓" });
    } else if (mode === "url") {
      toast({ description: "Image URL copied ✓" });
    } else {
      try {
        await navigator.clipboard?.writeText(url);
        toast({ description: "Image URL copied ✓" });
      } catch {
        toast({ description: "Could not copy image." });
      }
    }
  }

  const headerTitle = useMemo(
    () => (base?.name ? `${base.name} — Variants` : "Card Variants"),
    [base?.name]
  );

  if (!open) return null;

  return (
    <>
      {/* BACKDROP */}
      <div
        className="fixed inset-0 z-[220] bg-black/70"
        onClick={() => {
          if (zoomCard) { setZoomCard(null); return; }
          if (onClose) onClose(); else navigate(-1);
        }}
        aria-hidden
      />
      {/* SHELL */}
      <div
        className="fixed inset-0 z-[221] p-3 sm:p-6 flex flex-col items-center overflow-hidden"
        role="dialog"
        aria-modal="true"
        onClick={(e) => e.stopPropagation()}
      >
        <div
          className="w-full max-w-[min(1180px,96vw)] h-[100dvh] sm:h-auto sm:max-h-[calc(100vh-3rem)]
                     flex flex-col rounded-none sm:rounded-2xl border border-white/10
                     bg-gray-950/95 backdrop-blur shadow-2xl overflow-hidden"
        >
          {/* HEADER */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-white/10">
            <div className="font-semibold text-white text-base sm:text-lg truncate">
              {headerTitle}
            </div>
            <div className="flex items-center gap-2">
              {base && (
                <Button
                  variant="outline"
                  size="sm"
                  className="text-xs"
                  onClick={() => toggleWishlist(base as any)}
                >
                  <Heart className="w-4 h-4 mr-1" />
                  {wishlisted ? "Wishlisted" : "Wishlist"}
                </Button>
              )}
              <Button
                variant="ghost"
                size="sm"
                onClick={() => (onClose ? onClose() : navigate(-1))}
                aria-label="Close"
                className="text-white/80 hover:text-white"
              >
                <X className="w-5 h-5" />
              </Button>
            </div>
          </div>

          {/* SCROLL AREA */}
          <div className="flex-1 overflow-auto">
            <div className="px-4 pt-4 pb-10 space-y-6">
              {/* TOP IMAGE */}
              <div className="w-full max-w-md mx-auto aspect-[3/4] bg-black/80 rounded-lg overflow-hidden ring-1 ring-white/10">
                <img
                  src={getCardImageUrl(base)}
                  alt={base?.name || "Card"}
                  className="w-full h-full object-contain"
                />
              </div>

              {/* BASIC DETAILS (kept minimal) */}
              {loading ? (
                <div className="text-sm text-white/60">Loading variants…</div>
              ) : error ? (
                <div className="text-sm text-amber-300">{error}</div>
              ) : null}

              {/* Marketplace section (retained) */}
              {marketListings.length > 0 && (
                <div className="space-y-2">
                  <h3 className="text-sm font-semibold tracking-wide text-white/70 uppercase">
                    Marketplace Listings ({marketListings.length})
                  </h3>
                  <div className="grid gap-2 sm:grid-cols-2 max-h-72 overflow-auto rounded border border-white/10 p-2 bg-black/30">
                    {marketListings.map((l) => (
                      <div
                        key={l.id}
                        className="flex gap-2 rounded-md border border-white/10 bg-black/50 p-2 text-xs text-white/80"
                      >
                        <img
                          src={
                            l.image ||
                            (l.scryfall_id
                              ? ` + scryImageUrl(
                                  l.scryfall_id
                                , "small") + `
                              : "/images/sg-card-back.png")
                          }
                          alt={l.name}
                          className="h-14 w-10 object-cover rounded"
                        />
                        <div className="min-w-0 flex-1">
                          <div className="truncate font-medium">{l.name}</div>
                          <div className="truncate opacity-70">
                            {(l.set || "")?.toUpperCase?.()} {l.number ? `• #${l.number}` : ""}
                            {l.foil ? " • Foil" : ""}
                          </div>
                          <div className="mt-0.5 flex items-center gap-2">
                            <span
                              className={
                                l.status === "sale"
                                  ? "text-amber-300"
                                  : "text-cyan-300"
                              }
                            >
                              {l.status === "sale"
                                ? l.price != null
                                  ? `$${l.price.toFixed(2)}`
                                  : "Sale"
                                : "Trade"}
                            </span>
                            <button
                              className="ml-auto rounded border border-white/20 px-2 py-0.5 hover:bg-white/10"
                              onClick={() => {
                                window.location.href = `/${l.ownerUid}`;
                              }}
                            >
                              Profile
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* SET FILTER BAR */}
              <SetFilterBar
                variants={(variants ?? []) as Variant[]}
                selectedSet={selectedSet}
                onChange={setSelectedSet}
              />

              {/* VARIANTS GRID */}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <h3 className="text-sm font-semibold tracking-wide text-white/70 uppercase">
                    Variants {(variants?.length || 0) ? `(${variants?.length})` : ""}
                  </h3>
                  {loading && (
                    <div className="text-xs text-white/50">Loading…</div>
                  )}
                </div>

                {!loading && !error && (variants?.length || 0) === 0 && (
                  <div className="text-sm text-white/60">No variants found.</div>
                )}

                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3">
                  {(
                    (selectedSet === "all"
                      ? (variants ?? [])
                      : (variants ?? []).filter(
                          (v) => (v.set || "").toUpperCase() === selectedSet
                        )) as Variant[]
                  ).map((v) => {
                    const img =
                      v.card_faces?.[0]?.image_uris?.normal ||
                      v.image_uris?.normal ||
                      v.card_faces?.[0]?.image_uris?.large ||
                      v.image_uris?.large ||
                      v.card_faces?.[0]?.image_uris?.small ||
                      v.image_uris?.small ||
                      "";
                    const reg = v.prices?.usd
                      ? Number(v.prices.usd).toFixed(2)
                      : null;
                    const foil = v.prices?.usd_foil
                      ? Number(v.prices.usd_foil).toFixed(2)
                      : v.prices?.usd_etched
                      ? Number(v.prices.usd_etched).toFixed(2)
                      : null;
                    const glow = highlightNames.has(v.name.toLowerCase());
                    return (
                      <button
                        key={v.id}
                        type="button"
                        onClick={() => setZoomCard(v as any)}
                        className={`group relative flex flex-col rounded-lg overflow-hidden border bg-black/40 hover:border-white/40 transition
                          ${glow ? "v2-gold-glow border-amber-300/70" : "border-white/15"}`}
                        title={`${v.name} ${v.set?.toUpperCase?.() || ""} #${v.collector_number || ""}`}
                      >
                        <div className="aspect-[3/4] bg-black/70 overflow-hidden">
                          {img ? (
                            <img
                              src={img}
                              alt={v.name}
                              className="h-full w-full object-cover group-hover:scale-[1.03] transition-transform"
                              loading="lazy"
                            />
                          ) : (
                            <div className="grid h-full w-full place-items-center text-[10px] text-white/50">
                              No Image
                            </div>
                          )}
                        </div>
                        <div className="p-2 text-left space-y-1">
                          <div className="text-[12px] font-medium text-white truncate">
                            {v.set?.toUpperCase()} • #{v.collector_number}
                          </div>
                          <div className="flex items-center gap-2 text-[10px] text-white/70">
                            <span>{reg ? `$${reg}` : "—"}</span>
                            <span className="text-white/30">|</span>
                            <span className="text-purple-300">
                              {foil ? `$${foil}` : "—"}
                            </span>
                          </div>
                        </div>
                        {glow && (
                          <div className="absolute inset-0 pointer-events-none bg-gradient-to-br from-amber-400/0 via-amber-400/5 to-amber-500/0" />
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* ZOOM OVERLAY */}
        {zoomCard && (
          <>
            <div
              className="fixed inset-0 z-[230] bg-black/70"
              onClick={() => setZoomCard(null)}
              aria-hidden
            />
            <div
              className="fixed inset-0 z-[231] p-4 flex items-center justify-center"
              onClick={(e) => e.stopPropagation()}
            >
              <div
                className="relative w-full max-w-[min(520px,92vw)] rounded-xl border border-white/15 bg-gray-900/95 backdrop-blur
                           shadow-2xl overflow-hidden flex flex-col"
              >
                <div className="flex items-center justify-between px-4 py-2 border-b border-white/10">
                  <div className="text-sm font-semibold text-white truncate">
                    {zoomCard.name}
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setZoomCard(null)}
                    className="text-white/70 hover:text-white"
                  >
                    <X className="w-5 h-5" />
                  </Button>
                </div>
                <div className="px-4 pt-4 pb-3 flex flex-col gap-4 overflow-auto">
                  <div className="w-full aspect-[3/4] rounded-md bg-black overflow-hidden ring-1 ring-white/10">
                    <img
                      src={getCardImageUrl(zoomCard as any)}
                      alt={zoomCard.name}
                      className="w-full h-full object-contain"
                      draggable={false}
                    />
                  </div>
                  <div className="flex flex-wrap gap-3">
                    <Button
                      className="flex-1 bg-emerald-600/80 hover:bg-emerald-600 text-white font-semibold"
                      onClick={() => {
                        const v = zoomCard;
                        const reg = v.prices?.usd ? Number(v.prices.usd) : null;
                        const foil = v.prices?.usd_foil
                          ? Number(v.prices.usd_foil)
                          : v.prices?.usd_etched
                          ? Number(v.prices.usd_etched)
                          : null;
                        setSelectedPriceRegular(reg ?? null);
                        setSelectedPriceFoil(foil ?? null);
                        setAddFor(v);
                      }}
                    >
                      <Plus className="w-4 h-4 mr-1" />
                      Add
                    </Button>
                    <Button
                      variant="outline"
                      className="flex-1 border-pink-400/50 text-pink-200 hover:bg-pink-500/10"
                      onClick={() => toggleWishlist(zoomCard)}
                    >
                      <Heart className="w-4 h-4 mr-1" />
                      {wishlisted ? "Wishlisted" : "Favorite"}
                    </Button>
                    <Button
                      variant="outline"
                      className="flex-1 border-amber-400/50 text-amber-200 hover:bg-amber-500/10"
                      onClick={() => handleCopyImage(zoomCard)}
                    >
                      <Share2 className="w-4 h-4 mr-1" />
                      Copy Img
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}

        {/* Add To Collection Modal */}
        {addFor && (
          <div className="fixed inset-0 z-[540] pointer-events-none">
            <div className="pointer-events-auto">
              <AddToCollectionModal
                open
                card={{
                  id: addFor.id,
                  name: addFor.name,
                  oracle_id: addFor.oracle_id,
                  set: addFor.set,
                  collector_number: addFor.collector_number,
                  image_uris: addFor.image_uris,
                  card_faces: addFor.card_faces,
                } as any}
                initialPriceRegular={selectedPriceRegular}
                initialPriceFoil={selectedPriceFoil}
                onClose={() => setAddFor(null)}
                onAdded={() => setAddFor(null)}
              />
            </div>
          </div>
        )}
      </div>

      {/* STYLES (share gold glow with V2Modal) */}
      <style>{`
        .v2-gold-glow{
          position:relative;
          box-shadow:0 0 0 1px #f5c84b,0 0 6px 1px #f5c84b80,0 0 14px 3px #e1a90060;
          animation:v2GoldPulse 2.4s ease-in-out infinite;
        }
        @keyframes v2GoldPulse {
          0%,100%{box-shadow:0 0 0 1px #f5c84b,0 0 4px 0px #f5c84b66,0 0 12px 2px #e1a90040;}
          50%{box-shadow:0 0 0 1px #ffe38d,0 0 10px 4px #ffd966aa,0 0 22px 8px #f5c84b80;}
        }
        .sonner-toaster,
        [data-radix-toast-viewport],
        .radix-toast-viewport { z-index: 600 !important; }
      `}</style>
    </>
  );
}

/* -------- New: top filter bar + hook to compute filtered variants -------- */
function SetFilterBar({
  variants,
  selectedSet,
  onChange,
}: {
  variants: Variant[];
  selectedSet: string;
  onChange: (set: string) => void;
}) {
  const [open, setOpen] = useState(false);

  // Derive unique sets with counts
  const setOptions = useMemo(() => {
    const map = new Map<string, { code: string; name: string; count: number }>();
    for (const v of variants) {
      const code = (v.set || "").toString().toUpperCase();
      if (!code) continue;
      const name = (v.set_name || code).toString();
      const entry = map.get(code) || { code, name, count: 0 };
      entry.count++;
      map.set(code, entry);
    }
    const arr = Array.from(map.values()).sort((a, b) => a.name.localeCompare(b.name));
    return [{ code: "all", name: "All sets", count: variants.length }, ...arr];
  }, [variants]);

  return (
    <div className="sticky top-0 z-10 flex items-center gap-2 px-3 py-2 border-b border-slate-800 bg-gray-950/95 backdrop-blur">
      <label className="text-xs text-slate-300">
        Set
        <select
          className="ml-2 bg-gray-900 border border-slate-700 rounded-md px-2 py-1 text-white text-sm"
          value={selectedSet}
          onChange={(e) => onChange(e.target.value)}
        >
          {setOptions.map((o) => (
            <option key={o.code} value={o.code}>
              {o.name} {o.code !== "all" ? `(${o.count})` : ""}
            </option>
          ))}
        </select>
      </label>
      <div className="ml-auto text-xs text-slate-400">
        {selectedSet === "all"
          ? `${variants.length} results`
          : `${variants.filter((v) => (v.set || "").toUpperCase() === selectedSet).length} results`}
      </div>
    </div>
  );
}

// Helper you can drop near your map usage to filter without refactors:
// const displayedVariants = useMemo(
//   () => ((window as any).__variantsSelectedSet ?? "all") === "all"
//     ? variants
//     : variants.filter(v => (v.set || "").toUpperCase() === (window as any).__variantsSelectedSet),
//   [variants],
// );


